/* eslint-disable react/jsx-key */
import { createHash } from 'crypto';
import { Record } from 'immutable';
import type { Map as ImmutableMap, RecordOf } from 'immutable';
import { DateTime } from 'luxon';
import numeral from 'numeral';
import * as React from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useInterval, useKey, useToggle, useUpdate } from 'react-use';
import type { AnyAction } from 'redux';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuidv4 } from 'uuid';

import { accountsActions, accountsSelectors, accountsTypes } from 'app-components/flux/accounts';
import { authActions, authSelectors } from 'app-components/flux/auth';
import { bumpActions, bumpSelectors } from 'app-components/flux/bump';
import { bumpInterface } from 'app-components/flux/bump';
import { coreActions } from 'app-components/flux/core-actions';
import { deleteCreditProfile } from 'app-components/flux/credit-profile/actions';
import { getCreditProfile } from 'app-components/flux/credit-profile/selectors';
import { entitlementsSelectors } from 'app-components/flux/entitlements';
import { featureFlagsActions, featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { npsActions } from 'app-components/flux/nps';
import { preferencesActions, preferencesSelectors } from 'app-components/flux/preferences';
import { preferencesV2Actions, preferencesV2Selectors } from 'app-components/flux/preferences-v2';
import { profileSelectors } from 'app-components/flux/profile';
import { subscriptionsSelectors } from 'app-components/flux/subscriptions';
import * as tickerLogosActions from 'app-components/flux/ticker-logos/tickerLogosActions';
import { transactionsActions, transactionsSelectors, transactionsTypes } from 'app-components/flux/transactions';
import {
  crashReporterInterface,
  getEnvironmentConfig,
  getHostConfig,
  getLogger,
  localPreferences,
  loggerConfigurator,
} from 'app-components/utils/core';
import { getBuildConfig } from 'app-components/utils/core/config';
import { immutableSerialize } from 'app-components/utils/redux-store';

import AutoStoriesOutlinedIcon from '@mui/icons-material/AutoStoriesOutlined';
import Brightness4OutlinedIcon from '@mui/icons-material/Brightness4Outlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import ClearIcon from '@mui/icons-material/Clear';
import DoneAllOutlinedIcon from '@mui/icons-material/DoneAllOutlined';
import FavoriteBorderOutlinedIcon from '@mui/icons-material/FavoriteBorderOutlined';
import HeartBrokenOutlinedIcon from '@mui/icons-material/HeartBrokenOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import RemoveDoneIcon from '@mui/icons-material/RemoveDone';
import RemoveModeratorOutlinedIcon from '@mui/icons-material/RemoveModeratorOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItem from '@mui/material/ListItem';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import Tooltip from '@mui/material/Tooltip';
import Zoom from '@mui/material/Zoom';

import { usePreferenceV2 } from 'app-components/flux/preferences-v2';
import CypressLogo from 'assets/logos/cypress-logo.png';
import DispatchDialog from 'components/DispatchDialog';
import Dump from 'components/Dump';
import HighlightText from 'components/HighlightText';
import importAsync from 'components/ImportFromMint/importAsync';
import useQPreferences from 'components/QPreferences/useQPreferences';
import NestedMenuItem from 'components/QuickenControls/NestedMenuItem';
import RequestDialog from 'components/RequestDialog';
import ReusableComponentTag from 'containers/SharedComponentsPage/ReusableComponentTag';
import { useGlobalSubscriptionMessagingMock } from 'containers/SubscriptionMessaging';
import { appRefreshAll } from 'data/app/actions';
import { dispatchSimpleNotification } from 'data/notifications/notificationsUtils';
import { useAppStore } from 'hooks/redux';
import AxiosFactory from 'utils/axiosFactory';
import consoleMirror from 'utils/consoleMirror';
import * as sessionStorageEx from 'utils/sessionStorageEx';
import { saveStore } from 'utils/storeUtils';
import { getTTI, getTTIs } from 'utils/tti';

import FFMenu from './FFMenu';
import alertsPayload from './alertsPayload';
import allTaxLineItemsTransactions from './allTaxLineItemsTransactions';
import { useSharedStateBump } from './bumpSagas';
import investmentHoldingsSample from './investmentHoldingsSample';
import investmentInvalidTransactions from './investmentInvalidTransactions';
import investmentTransactions from './investmentTransactions';
import loadTestTransactionYears from './loadTestTransactionYears';
import loadTestTransactions from './loadTestTransactions';
import missingTaxLineItemsCategories from './missingTaxLineItemsCategories';
import postedTransactions from './postedTransactions';
import recurringTransactions from './recurringTransactions';
import taxPaymentTransactions from './taxPaymentTransactions';

// const relyingPartyId = 'localhost';
// navigator.credentials
//   .get({
//     mediation: 'required',
//     publicKey: {
//       challenge: new Uint8Array([
//         139, 66, 181, 87, 7, 203, 139, 66, 181, 87, 7, 203, 139, 66, 181, 87, 7, 203, 139, 66, 181, 87, 7, 203, 139, 66,
//         181, 87, 7, 203, 1, 2,
//       ]),
//       rpId: 'localhost',
//       allowCredentials: [
//         {
//           type: 'public-key',
//           id: new Uint8Array([79, 252, 83, 72, 214, 7, 89, 26]), // 64 bytes max
//           transports: ['internal'],
//         },
//       ],
//       userVerification: 'required',
//     },
//   })
//   .then((publicKeyCredential) => {
//     debugger;
//     const response = publicKeyCredential.response;

//     // Access authenticator data ArrayBuffer
//     const authenticatorData = response.authenticatorData;

//     // Access client JSON
//     const clientJSON = response.clientDataJSON;

//     // Access signature ArrayBuffer
//     const signature = response.signature;

//     // Access userHandle ArrayBuffer
//     const userHandle = response.userHandle;
//   })
//   .catch((error) => {
//     assert(false, error);
//     debugger;
//     navigator.credentials
//       .create({
//         publicKey: {
//           challenge: new Uint8Array([
//             117, 61, 252, 231, 191, 241, 1, 214, 221, 15, 244, 54, 51, 62, 64, 11, 117, 61, 252, 231, 191, 241, 1, 214,
//             121, 15, 222, 54, 51, 62, 64, 11,
//           ]), // 32 bytes
//           rp: { id: 'localhost', name: 'ACME Corporation' },
//           user: {
//             id: new Uint8Array([79, 252, 83, 72, 214, 7, 89, 26]), // 64 bytes max
//             name: 'jamiedoe',
//             displayName: 'Jamie Doe',
//           },
//           pubKeyCredParams: [{ type: 'public-key', alg: -7 }],
//           authenticatorSelection: {
//             authenticatorAttachment: 'platform',
//             userVerification: 'required',
//           },
//           // timeout: 60000,  // Timeout in milliseconds
//           attestation: 'none',
//         },
//       })
//       .then((publicKeyCredential) => {
//         debugger;
//         const response = publicKeyCredential?.response;

//         // Access attestationObject ArrayBuffer
//         const attestationObj = response.attestationObject;

//         // Access client JSON
//         const clientJSON = response.clientDataJSON;

//         // Return authenticator data ArrayBuffer
//         const authenticatorData = response.getAuthenticatorData();

//         // Return public key ArrayBuffer
//         const pk = response.getPublicKey();

//         // Return public key algorithm identifier
//         const pkAlgo = response.getPublicKeyAlgorithm();

//         // Return permissible transports array
//         const transports = response.getTransports();
//       })
//       .catch((error) => assert(false, error));
//   });

const logger = getLogger('devMenu');

const useStyles = makeStyles()(() => ({
  tooltipPopper: {
    maxWidth: 130,
  },
  tooltipBump: {
    maxWidth: 250,
  },
}));

const DevMenu = React.memo(() => {
  const store = useAppStore();
  const dispatch = useDispatch();
  const menuButtonRef = React.useRef<any>();
  const update = useUpdate();
  const { classes } = useStyles();

  const bumpSocketState = useSelector(bumpSelectors.getBumpSocketState);
  const [sharedStateBump] = useSharedStateBump();
  const bumpCount = sharedStateBump.count;
  const [bumpZoomIn, setBumpZoomIn] = React.useState(true);
  React.useEffect(() => {
    setBumpZoomIn(false);
    setTimeout(() => setBumpZoomIn(true), 200);
  }, [bumpCount]);
  const bumpStats = React.useMemo(
    () =>
      sharedStateBump.actions
        .slice(-10)
        .map(
          (action, index) =>
            `${action.payload.type}:${action.payload.data.resourceType}${
              index === sharedStateBump.actions.length - 1 ? '(last)' : ''
            }`,
        )
        .concat([`Bump Statistics (last of ${sharedStateBump.count} messages):`])
        .concat([`Bump Socket State: ${bumpSocketState}`])
        .reverse()
        .join(' '),
    [sharedStateBump, bumpSocketState],
  );

  const [subscriptionMessageMock, setSubscriptionMessageMock] = useGlobalSubscriptionMessagingMock();
  const [openMenu, toggleMenu] = useToggle(false);
  const [showDevMenuButton, setShowDevMenuButton] = React.useState(true);
  const [showLoadDB, setShowLoadDB] = React.useState(false);
  const [showImport, setShowImport] = React.useState(false);
  const [showRequestDialog, setShowRequestDialog] = React.useState(false);
  const [dispatchDialog, setDispatchDialog] = React.useState<{ type?: string; payload?: RecordOf<any>; meta?: any }>(
    {},
  );
  const [showDispatchDialog, setShowDispatchDialog] = React.useState(false);
  const [preferenceSearchKey, setPreferenceSearchKey] = React.useState<string>();

  const { datasetPreferences, setDatasetPreference } = useQPreferences();
  const themeKey = datasetPreferences?.theme;
  const authSession: any = useSelector(authSelectors.getAuthSession);
  const datasetId: string = useSelector(authSelectors.getDatasetId);
  const profile: any = useSelector(profileSelectors.getProfile);
  const subscription: any = useSelector(subscriptionsSelectors.getActiveSubscription);
  const entitlement: any = useSelector(entitlementsSelectors.getTopTierEntitlement);
  const preferencesV2: any = useSelector(preferencesV2Selectors.getPreferencesV2);
  const popupMessageDismissedOnISO: string = useSelector((state) =>
    preferencesSelectors.getSharedPreferencesByPath(state, { group: 'user', path: undefined }),
  )?.subscriptionMessageDismissedOn;
  const transactions: ImmutableMap<string, any> = useSelector(transactionsSelectors.getResourcesById);
  const transactionsIsLoading: boolean = useSelector(transactionsSelectors.getIsLoading);
  const creditProfileData = useSelector(getCreditProfile);
  // @ts-expect-error FIXME TS2345
  const ldHasDatasetId = useSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'ldHasDatasetId'));

  const [requestDialog, setRequestDialog] = React.useState<{
    requestMethod?: 'GET' | 'PUT' | 'POST' | 'DELETE';
    requestPath?: string;
    requestQuery?: any;
    requestBody?: any;
  }>({
    requestMethod: 'GET',
    requestPath: '/accounts',
    requestQuery: undefined,
    requestBody: undefined,
  });

  useInterval(update, 3000); // update memory usage on post click idle
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore TS2339
  // eslint-disable-next-line no-unsafe-optional-chaining
  const memoryUsedMB = Math.round(window?.performance?.memory?.usedJSHeapSize / (1024 * 1024));
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore TS2339
  // eslint-disable-next-line no-unsafe-optional-chaining
  const memoryLimitMB = Math.round(window?.performance?.memory?.jsHeapSizeLimit / (1024 * 1024));

  const onTest = React.useCallback(() => {
    dispatchSimpleNotification('test');
    const transactions = transactionsSelectors.getResourcesById(store.getState());
    const transaction = transactions.first();
    dispatch(
      transactionsActions.updateTransaction(
        transactionsTypes.mkCashFlowTransactionPatch({
          coa: transaction?.coa,
          id: transaction?.id,
          amount: (transaction?.amount ?? 0) + 1,
          // memo: `${transaction?.memo ?? ''}1`,
        }),
      ),
    );
  }, [dispatch, store]);
  useKey((event) => event.metaKey && event.code === 'KeyB', onTest, { event: 'keypress' });

  const tti = getTTI();
  const ttis = getTTIs();
  const stats =
    '' +
    `${!Number.isNaN(memoryUsedMB) && !Number.isNaN(memoryLimitMB) && `${memoryUsedMB}MB of ${memoryLimitMB}MB`}` +
    ` transactions: ${numeral(transactions.size).format('0a')}...` +
    ` tti: ${numeral(tti).format('0a')}ms (${ttis.length}cnt)`;

  const [ffAnchorEl, setFFAnchorEl] = React.useState<(EventTarget & HTMLElement) | undefined>();
  const featureFlagsOverrides = localPreferences.getItem('featureFlagsOverrides') || {};
  const featureFlagsOverridesCount = Object.keys(featureFlagsOverrides).length;
  const [sPrivacyMode, setSPrivacyMode] = usePreferenceV2('sPrivacyMode');

  return (
    <>
      <Zoom in={showDevMenuButton}>
        <Stack
          sx={{
            zIndex: 1350,
            borderRadius: '12px 0 0 12px',
            position: 'fixed',
            right: 0,
            bottom: 160,
            backdropFilter: 'blur(5px)',
          }}
        >
          <Tooltip placement='top' title={stats} classes={{ popper: classes.tooltipPopper }}>
            <Badge
              badgeContent={
                <Box>
                  {numeral(transactions.size).format('0a')}
                  {transactionsIsLoading && (
                    <CircularProgress size={20} sx={{ position: 'absolute', top: 0, left: 0 }} />
                  )}
                </Box>
              }
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
            >
              <Badge
                badgeContent={ttis[ttis.length - 1]}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                max={999}
              >
                <ToggleButton
                  id='dev-menu'
                  ref={menuButtonRef}
                  value='fixingError'
                  aria-label='DevMenu'
                  role='complementary'
                  onClick={() => toggleMenu()}
                  size='small'
                >
                  <BuildOutlinedIcon />
                  {!Number.isNaN(memoryUsedMB) && !Number.isNaN(memoryLimitMB) && (
                    <CircularProgress
                      variant='determinate'
                      value={(100 * memoryUsedMB) / memoryLimitMB}
                      sx={{ position: 'absolute' }}
                    />
                  )}
                </ToggleButton>
              </Badge>
            </Badge>
          </Tooltip>

          <ToggleButton
            id='theme-button'
            size='small'
            value={themeKey || 'ACME_DARK'}
            selected={themeKey === 'ACME_DARK'}
            onChange={(event, value) =>
              setDatasetPreference({ theme: value === 'ACME_DARK' ? 'simplifiDefault' : 'ACME_DARK' })
            }
          >
            <Brightness4OutlinedIcon />
          </ToggleButton>

          <Tooltip
            placement='left'
            title={
              ldHasDatasetId
                ? `Launch Darkly is connected (${featureFlagsOverridesCount} overrides)`
                : 'Launch Darkly is NOT connected'
            }
          >
            <Badge
              max={99}
              badgeContent={featureFlagsOverridesCount || ''}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
            >
              <ToggleButton
                id='ld-status'
                data-testid='ld-status'
                size='small'
                value={ldHasDatasetId ? 'ld-connected' : 'ld-disconnected'}
                onClick={(event) => setFFAnchorEl(ffAnchorEl ? undefined : event.currentTarget)}
              >
                {ldHasDatasetId ? (
                  <DoneAllOutlinedIcon color={featureFlagsOverridesCount ? 'info' : 'inherit'} />
                ) : (
                  <RemoveDoneIcon color='error' />
                )}
              </ToggleButton>
            </Badge>
          </Tooltip>

          <ToggleButton
            id='storybook-button'
            size='small'
            value='storybook'
            onClick={() => window.open(getHostConfig().storybook, '_blank')}
          >
            <AutoStoriesOutlinedIcon />
          </ToggleButton>

          <Tooltip placement='left' title={bumpStats} classes={{ popper: classes.tooltipBump }}>
            <Badge
              max={9999}
              badgeContent={numeral(sharedStateBump?.count).format('0a')}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
            >
              <ToggleButton
                id='websocket-button'
                size='small'
                value={sharedStateBump?.count}
                onClick={() => {
                  bumpInterface.close(1000, 'dev-menu');
                }}
              >
                <Zoom in={bumpZoomIn}>
                  {bumpSocketState === 'AUTHENTICATED' ? (
                    <FavoriteBorderOutlinedIcon />
                  ) : (
                    <HeartBrokenOutlinedIcon color='error' />
                  )}
                </Zoom>
              </ToggleButton>
            </Badge>
          </Tooltip>

          <ToggleButton id='privacy-button' size='small' value='privacy' onClick={() => setSPrivacyMode(!sPrivacyMode)}>
            {sPrivacyMode ? <SecurityOutlinedIcon /> : <RemoveModeratorOutlinedIcon />}
          </ToggleButton>
        </Stack>
      </Zoom>

      {!ffAnchorEl ? null : (
        <FFMenu anchorEl={ffAnchorEl} open={Boolean(ffAnchorEl)} onClose={() => setFFAnchorEl(undefined)} />
      )}

      {showDispatchDialog && (
        <DispatchDialog
          open
          onClose={() => setShowDispatchDialog(false)}
          type={dispatchDialog.type}
          payload={dispatchDialog.payload}
          meta={dispatchDialog.meta}
        />
      )}

      {showRequestDialog && (
        <RequestDialog
          open
          onHandleClose={() => setShowRequestDialog(false)}
          method={requestDialog.requestMethod}
          path={requestDialog.requestPath}
          query={requestDialog.requestQuery}
          body={requestDialog.requestBody}
        />
      )}

      {showLoadDB && (
        <Dialog open onClose={() => setShowLoadDB(false)}>
          <input
            style={{ margin: 40 }}
            id='file-input'
            type='file'
            onChange={(event) => {
              const file = event?.target?.files?.[0];
              file?.text().then((json) => {
                const state = immutableSerialize.hydrateImmutable(json);
                store?.dispatch(coreActions.setRoot(state) as AnyAction);
                sessionStorageEx.setSandbox(true);
                dispatch(
                  featureFlagsActions.applyFeatureFlagsChanges({
                    debug: true,
                  }),
                );
              });
            }}
          />
        </Dialog>
      )}

      {showImport && (
        <Dialog open onClose={() => setShowImport(false)}>
          <input
            style={{ margin: 40 }}
            id='file-import'
            type='file'
            onChange={(event) => {
              const file = event?.target?.files?.[0];
              file?.text().then((json) => {
                const fileJS = JSON.parse(json);
                importAsync(fileJS);
              });
            }}
          />
        </Dialog>
      )}

      <Tooltip title='Cypress is enabled'>
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-ignore TS2339 */}
        <Zoom in={window?.top?.Cypress}>
          <img
            alt='cypress'
            src={CypressLogo}
            style={{
              zIndex: 9999,
              position: 'fixed',
              right: 8,
              bottom: 132,
              opacity: 0.2,
              width: 28,
              height: 28,
            }}
          />
        </Zoom>
      </Tooltip>

      {openMenu && (
        <Menu
          id='dev-menu'
          open={openMenu}
          onClose={() => toggleMenu(false)}
          anchorEl={menuButtonRef.current}
          anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
          transformOrigin={{ horizontal: 'left', vertical: 'bottom' }}
          style={{ opacity: 0.9, left: -32 }}
        >
          <MenuItem
            dense
            key='Mint API import'
            onClick={(event) => {
              setShowImport(true);
              toggleMenu(false);
              event.stopPropagation();
            }}
          >
            Mint API import
          </MenuItem>
          <Divider />
          <NestedMenuItem
            key='copy-user-data-menu'
            onClick={() => {
              toggleMenu(false);
              const accounts = accountsSelectors.getResourcesById(store.getState());
              const message =
                `user id: ${authSession?.qcsId}\n` +
                `dataset id: ${datasetId}\n` +
                `email: ${profile?.username}\n` +
                `subscription: ${subscription?.productLineName} - ${subscription?.tierName} (${subscription?.expireOn})\n` +
                `entitlement: ${entitlement?.id} (${entitlement?.expiresOn})\n` +
                `accounts: ${accounts.size}\n` +
                `transactions: ${transactions.size}\n`;
              navigator.clipboard.writeText(message);
              dispatchSimpleNotification(message);
            }}
            menuItems={[
              <MenuItem
                dense
                key='Save-DB'
                onClick={(event) => {
                  saveStore('store.json', false);
                  toggleMenu(false);
                  event.stopPropagation();
                }}
              >
                Save DB
              </MenuItem>,
              <MenuItem
                dense
                key='Save-DB-Sanitized'
                onClick={(event) => {
                  saveStore('store.json', true);
                  toggleMenu(false);
                  event.stopPropagation();
                }}
              >
                Save DB Sanitized
              </MenuItem>,
              <MenuItem
                dense
                key='Load-DB'
                onClick={(event) => {
                  setShowLoadDB(true);
                  toggleMenu(false);
                  event.stopPropagation();
                }}
              >
                Load DB
              </MenuItem>,
              <Divider />,
              <MenuItem
                dense
                key='copy-persistor-key'
                onClick={(event) => {
                  navigator.clipboard.writeText(createHash('sha256').update(datasetId).digest('hex'));
                  toggleMenu(false);
                  event.stopPropagation();
                }}
              >
                Copy Persistor Key
              </MenuItem>,
              <Divider />,
              <MenuItem
                dense
                key='Console-Logs'
                onClick={(event) => {
                  const logs = consoleMirror.log.reverse().join('\n');
                  alert(logs); // eslint-disable-line no-alert
                  toggleMenu(false);
                  event.stopPropagation();
                }}
              >
                Console Logs
              </MenuItem>,
            ]}
          >
            Copy User Data <br /> ({authSession?.qcsId} - {datasetId})
          </NestedMenuItem>

          <Divider />

          <NestedMenuItem
            // @ts-expect-error FIXME TS7006
            onKeyDown={(event) => event.stopPropagation()}
            // @ts-expect-error FIXME TS7006
            onKeyUp={(event) => event.stopPropagation()}
            // @ts-expect-error FIXME TS7006
            onKeyPress={(event) => event.stopPropagation()}
            menuItems={[
              <MenuItem dense key='searchbar'>
                <TextField
                  label='Type to Search'
                  type='search'
                  defaultValue=''
                  fullWidth
                  autoFocus
                  onChange={(event) => setPreferenceSearchKey(event.target.value?.toLowerCase?.())}
                />
              </MenuItem>,
              <Divider />,
              ...preferencesV2
                .filter(
                  // @ts-expect-error FIXME TS7006
                  (preference) =>
                    !preferenceSearchKey ||
                    preference.label?.toLowerCase?.()?.includes(preferenceSearchKey) ||
                    preference.name?.toLowerCase?.()?.includes(preferenceSearchKey) ||
                    preference.value?.toLowerCase?.()?.includes(preferenceSearchKey),
                )
                .valueSeq()
                // @ts-expect-error FIXME TS7006
                .sort((preference1, preference2) => preference1.label?.localeCompare(preference2.label))
                // @ts-expect-error FIXME TS7006
                .map((preference) => {
                  const isJSON = preference.dataType === 'JSON' || preference.dataType === 'ARRAY';
                  return (
                    <MenuItem dense key={preference.id}>
                      <Box display='flex' alignItems='center' justifyContent='flex-end' width='100%'>
                        <Dump value={preference} />
                        <HighlightText
                          text={`${preference.label} (${preference.name})`}
                          searchKeys={[preferenceSearchKey]}
                        />
                        &nbsp;&nbsp;&nbsp;
                        <TextField
                          defaultValue={isJSON ? JSON.stringify(preference.value) : preference.value}
                          onBlur={(event) => {
                            if (preference.value !== event.target.value) {
                              dispatch(
                                preferencesV2Actions.updatePreferenceOverride(
                                  {
                                    preferenceName: preference.name,
                                    value: isJSON ? JSON.parse(event.target.value) : event.target.value,
                                    id: preference.name,
                                    dataType: preference.dataType,
                                  },
                                  { undo: { userMessage: 'Preference Updated' } },
                                ),
                              );
                              dispatchSimpleNotification('Preference Updated'); // remove once prefs/v2 undo implemented
                            }
                          }}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                              // @ts-ignore
                              event.target.blur();
                            }
                          }}
                        />
                        <IconButton
                          size='small'
                          onClick={() => {
                            dispatch(preferencesV2Actions.resetPreferenceOverride({ id: preference?.name }));
                            dispatchSimpleNotification('Preference Reset'); // remove once prefs/v2 undo implemented
                          }}
                        >
                          <ClearIcon />
                        </IconButton>
                      </Box>
                    </MenuItem>
                  );
                }),
              <Divider />,
            ]}
          >
            <IconButton onClick={() => dispatch(preferencesV2Actions.getPreferencesV2())}>
              <RefreshIcon />
            </IconButton>
            Preferences V2
          </NestedMenuItem>

          <Divider />

          <NestedMenuItem
            dense
            id='request-menu'
            // @ts-expect-error FIXME TS7006
            onClick={(event) => {
              if (event.target === event.currentTarget) setShowRequestDialog(true);
              toggleMenu(false);
            }}
            menuItems={[
              <NestedMenuItem
                id='qcs-web-socket-menu'
                dense
                menuItems={[
                  <MenuItem
                    id='custom'
                    dense
                    onClick={(event) => {
                      event.stopPropagation();
                      dispatch(
                        bumpActions.sendBumpMessage({
                          hello: 'world',
                        }),
                      );
                    }}
                  >
                    Custom Message
                  </MenuItem>,
                ]}
              >
                QCS web socket
              </NestedMenuItem>,
              <Divider />,
              <MenuItem
                id='inject-missing-tax-line-categories'
                dense
                onClick={(event) => {
                  event.stopPropagation();
                  setRequestDialog({
                    requestMethod: 'PUT',
                    requestPath: '/categories',
                    requestQuery: undefined,
                    requestBody: JSON.stringify(missingTaxLineItemsCategories(), null, 2),
                  });
                  setShowRequestDialog(true);
                }}
              >
                Missing Tax Line Categories
              </MenuItem>,
              <MenuItem
                id='inject-all-tax-line-item-transactions'
                dense
                onClick={(event) => {
                  event.stopPropagation();
                  setRequestDialog({
                    requestMethod: 'PUT',
                    requestPath: '/transactions',
                    requestQuery: undefined,
                    requestBody: JSON.stringify(allTaxLineItemsTransactions(), null, 2),
                  });
                  setShowRequestDialog(true);
                }}
              >
                All Tax Line Items Transactions
              </MenuItem>,
              <Divider />,
              <NestedMenuItem
                id='inject-transactions'
                dense
                menuItems={[
                  <MenuItem
                    id='inject-tax-transactions'
                    dense
                    onClick={(event) => {
                      event.stopPropagation();
                      const account = accountsSelectors.getBankAccounts(store.getState()).first();
                      setRequestDialog({
                        requestMethod: 'POST',
                        requestPath: '/transactions/test-aggregated-importer',
                        requestQuery: undefined,
                        requestBody: JSON.stringify(
                          {
                            inTesting: true,
                            accountId: account?.id,
                            pendingAggregationType: 'CURRENT_PENDING_LIST',
                            transactions: taxPaymentTransactions(account),
                            cpData: {
                              balance: 2000.01,
                              balanceAt: DateTime.local().toISO(),
                            },
                          },
                          null,
                          2,
                        ),
                      });
                      setShowRequestDialog(true);
                    }}
                  >
                    Inject Tax Payments
                  </MenuItem>,
                  <MenuItem
                    id='inject-investment-transactions'
                    dense
                    onClick={(event) => {
                      event.stopPropagation();
                      const account = accountsSelectors.getInvestmentAccounts(store.getState()).first();
                      setRequestDialog({
                        requestMethod: 'POST',
                        requestPath: '/transactions/test-aggregated-importer',
                        requestQuery: undefined,
                        requestBody: JSON.stringify(
                          {
                            inTesting: true,
                            accountId: account?.id,
                            pendingAggregationType: 'CURRENT_PENDING_LIST',
                            transactions: investmentTransactions(account),
                            cpData: {
                              balance: 2000.01,
                              balanceAt: DateTime.local().toISO(),
                            },
                          },
                          null,
                          2,
                        ),
                      });
                      setShowRequestDialog(true);
                    }}
                  >
                    Inject Investment Transaction
                  </MenuItem>,
                  <MenuItem
                    id='inject-investment-invalid-transactions'
                    dense
                    onClick={(event) => {
                      event.stopPropagation();
                      const account = accountsSelectors.getInvestmentAccounts(store.getState()).first();
                      setRequestDialog({
                        requestMethod: 'POST',
                        requestPath: '/transactions/test-aggregated-importer',
                        requestQuery: undefined,
                        requestBody: JSON.stringify(
                          {
                            inTesting: true,
                            accountId: account?.id,
                            pendingAggregationType: 'CURRENT_PENDING_LIST',
                            transactions: investmentInvalidTransactions(account),
                            cpData: {
                              balance: 2000.01,
                              balanceAt: DateTime.local().toISO(),
                            },
                          },
                          null,
                          2,
                        ),
                      });
                      setShowRequestDialog(true);
                    }}
                  >
                    Inject Investment Invalid Transaction
                  </MenuItem>,
                  <MenuItem
                    id='inject-transaction-pending'
                    dense
                    onClick={(event) => {
                      event.stopPropagation();
                      const account = accountsSelectors.getCashAccounts(store.getState()).first();
                      const postedOnDate = DateTime.local().minus({ days: 1 });
                      const postedOn = postedOnDate.toISODate();
                      const cpId = uuidv4();
                      setRequestDialog({
                        requestMethod: 'POST',
                        requestPath: '/transactions/test-aggregated-importer',
                        requestQuery: undefined,
                        requestBody: JSON.stringify(
                          {
                            inTesting: true,
                            accountId: account?.id,
                            pendingAggregationType: 'CURRENT_PENDING_LIST',
                            cpData: {
                              balance: -56.78,
                              balanceAt: DateTime.local().toISO(),
                            },
                            transactions: [
                              {
                                source: 'QCS_REFRESH_BANK_PENDING',
                                accountId: account?.id,
                                postedOn,
                                payee: 'Safeway Grocceries',
                                coa: {
                                  type: 'UNCATEGORIZED',
                                  id: '0',
                                },
                                amount: -56.78,
                                state: 'PENDING',
                                matchState: 'NOT_MATCHED',
                                cpData: {
                                  id: cpId,
                                  cpPendingId: cpId,
                                  postedOn,
                                  txnOn: postedOn,
                                  amount: -56.78,
                                  inferredPayee: 'Safeway Grocceries',
                                  inferredCoa: {
                                    type: 'UNCATEGORIZED',
                                    id: '0',
                                  },
                                  cpCategoryId: '2002',
                                },
                                type: 'CASH_FLOW',
                              },
                            ],
                          },
                          null,
                          2,
                        ),
                      });
                      setShowRequestDialog(true);
                    }}
                  >
                    Inject Pending Transaction
                  </MenuItem>,
                  <MenuItem
                    id='inject-posted-transaction'
                    dense
                    onClick={(event) => {
                      event.stopPropagation();
                      const account = accountsSelectors.getCashAccounts(store.getState()).first();
                      setRequestDialog({
                        requestMethod: 'POST',
                        requestPath: '/transactions/test-aggregated-importer',
                        requestQuery: undefined,
                        requestBody: JSON.stringify(
                          {
                            inTesting: true,
                            accountId: account?.id,
                            pendingAggregationType: 'CURRENT_PENDING_LIST',
                            cpData: {
                              balance: -55.66,
                              balanceAt: DateTime.local().toISO(),
                            },
                            transactions: postedTransactions(account),
                          },
                          null,
                          2,
                        ),
                      });
                      setShowRequestDialog(true);
                    }}
                  >
                    Inject Posted Transaction
                  </MenuItem>,
                  <MenuItem
                    id='inject-recurring-transactions'
                    dense
                    onClick={(event) => {
                      event.stopPropagation();
                      const account = accountsSelectors.getCashAccounts(store.getState()).first();
                      setRequestDialog({
                        requestMethod: 'POST',
                        requestPath: '/pfm-importer/apple-wallet/import',
                        requestQuery: undefined,
                        requestBody: JSON.stringify(
                          {
                            resources: recurringTransactions(account),
                          },
                          null,
                          2,
                        ),
                      });
                      setShowRequestDialog(true);
                    }}
                  >
                    Inject Recurring Transactions
                  </MenuItem>,
                  <MenuItem
                    id='inject-load-test-transactions'
                    dense
                    onClick={(event) => {
                      event.stopPropagation();
                      const account = accountsSelectors.getCashAccounts(store.getState()).first();
                      setRequestDialog({
                        requestMethod: 'POST',
                        requestPath: '/transactions/test-aggregated-importer',
                        requestQuery: undefined,
                        requestBody: JSON.stringify(
                          {
                            inTesting: true,
                            accountId: account?.id,
                            pendingAggregationType: 'CURRENT_PENDING_LIST',
                            cpData: {
                              balance: -55.66,
                              balanceAt: DateTime.local().toISO(),
                            },
                            transactions: loadTestTransactions(),
                          },
                          null,
                          2,
                        ),
                      });
                      setShowRequestDialog(true);
                    }}
                  >
                    Inject Load Test Transactions
                  </MenuItem>,
                  <MenuItem
                    id='inject-15y-test-transactions'
                    dense
                    onClick={(event) => {
                      event.stopPropagation();
                      const account = accountsSelectors.getCashAccounts(store.getState()).first();
                      setRequestDialog({
                        requestMethod: 'POST',
                        requestPath: '/transactions/test-aggregated-importer',
                        requestQuery: undefined,
                        requestBody: JSON.stringify(
                          {
                            inTesting: true,
                            accountId: account?.id,
                            pendingAggregationType: 'CURRENT_PENDING_LIST',
                            cpData: {
                              balance: -55.66,
                              balanceAt: DateTime.local().toISO(),
                            },
                            transactions: loadTestTransactionYears(20),
                          },
                          null,
                          2,
                        ),
                      });
                      setShowRequestDialog(true);
                    }}
                  >
                    Inject 20 Years Transactions
                  </MenuItem>,
                ]}
              >
                Inject Transaction
              </NestedMenuItem>,
              <Divider />,
              <MenuItem
                id='inject-top-holdings'
                dense
                onClick={(event) => {
                  event.stopPropagation();
                  setRequestDialog({
                    requestMethod: 'PUT',
                    requestPath: '/v2/investments/holdings',
                    requestQuery: undefined,
                    requestBody: JSON.stringify({ resources: investmentHoldingsSample() }, null, 2),
                  });
                  setShowRequestDialog(true);
                }}
              >
                Inject top investment holdings
              </MenuItem>,
              <Divider />,
              <MenuItem
                id='delete-dataset'
                dense
                onClick={(event) => {
                  event.stopPropagation();
                  setRequestDialog({
                    requestMethod: 'DELETE',
                    requestPath: `/datasets/${datasetId}`,
                    requestQuery: undefined,
                    requestBody: undefined,
                  });
                  setShowRequestDialog(true);
                }}
              >
                Delete Dataset
              </MenuItem>,
              <Divider />,
              <MenuItem
                id='reports-configuration-request'
                dense
                onClick={(event) => {
                  event.stopPropagation();
                  setRequestDialog({
                    requestMethod: 'POST',
                    requestPath: '/reports/report-configuration',
                    requestQuery: undefined,
                    requestBody: JSON.stringify(
                      {
                        name: 'Income/Expense Report',
                        reportType: 'SUMMARY',
                        rowType: 'CATEGORY',
                        columnType: 'TIME',
                      },
                      null,
                      2,
                    ),
                  });
                  setShowRequestDialog(true);
                }}
              >
                Summary Report Configuration
              </MenuItem>,
              <NestedMenuItem
                id='alerts-menu'
                dense
                menuItems={[
                  <MenuItem
                    id='alerts-all'
                    dense
                    onClick={(event) => {
                      event.stopPropagation();
                      alertsPayload().forEach((alert) => {
                        const qcsAxios = AxiosFactory.get('qcs');
                        qcsAxios.post(
                          `${getEnvironmentConfig().services_url}/alert/simulate`,
                          JSON.stringify({
                            eventOn: DateTime.local().toISODate(),
                            source: 'TEST',
                            serviceKey: 'KEY1',
                            ...alert,
                          }),
                        );
                      });
                    }}
                  >
                    Simulate All ({alertsPayload().length})
                  </MenuItem>,
                  <Divider />,
                  alertsPayload()
                    .sort((alert1, alert2) =>
                      // @ts-expect-error FIXME TS2551
                      `${alert1.alertType}${alert1.alertSubType}`.localeCompare(
                        // @ts-expect-error FIXME TS2551
                        `${alert2.alertType}${alert2.alertSubType}`,
                      ),
                    )
                    .map((alert) => (
                      <MenuItem
                        // @ts-expect-error FIXME TS2551
                        id={`${alert.alertType}${alert.alertSubType}`}
                        dense
                        onClick={() => {
                          setRequestDialog({
                            requestMethod: 'POST',
                            requestPath: '/alert/simulate',
                            requestQuery: undefined,
                            requestBody: JSON.stringify(
                              {
                                ...alert,
                                eventOn: DateTime.local().toISODate(),
                                source: 'TEST',
                                serviceKey: 'KEY1',
                              },
                              null,
                              2,
                            ),
                          });
                          setShowRequestDialog(true);
                        }}
                      >
                        Simulate {alert.alertType} {alert.alertSubtype}
                      </MenuItem>
                    )),
                ]}
              >
                Alerts
              </NestedMenuItem>,
              <NestedMenuItem
                dense
                menuitems={[
                  <MenuItem
                    dense
                    onClick={() => {
                      setRequestDialog({
                        requestMethod: 'GET',
                        requestPath: '/accounts',
                        requestQuery: undefined,
                        requestBody: undefined,
                      });
                      setShowRequestDialog(true);
                    }}
                  >
                    Get All
                  </MenuItem>,
                ]}
              >
                Accounts
              </NestedMenuItem>,
              <NestedMenuItem
                id='alerts-menu'
                dense
                menuItems={[
                  <MenuItem
                    id='alerts-all'
                    dense
                    onClick={(event) => {
                      event.stopPropagation();
                      setRequestDialog({
                        requestMethod: 'GET',
                        requestPath: '/institution-logins/clear-aasr',
                        requestQuery: undefined,
                        requestBody: undefined,
                      });
                      setShowRequestDialog(true);
                    }}
                  >
                    Clear AASR
                  </MenuItem>,
                ]}
              >
                Institution Logins
              </NestedMenuItem>,
              <MenuItem
                onClick={(event) => {
                  event.stopPropagation();
                  setRequestDialog({
                    requestMethod: 'GET',
                    requestPath: '/preferences/user',
                    requestQuery: undefined,
                    requestBody: undefined,
                  });
                  setShowRequestDialog(true);
                }}
              >
                User Preferences
              </MenuItem>,
              <MenuItem
                onClick={(event) => {
                  event.stopPropagation();
                  setRequestDialog({
                    requestMethod: 'GET',
                    requestPath: '/preferences',
                    requestQuery: undefined,
                    requestBody: undefined,
                  });
                  setShowRequestDialog(true);
                }}
              >
                Dataset Preferences
              </MenuItem>,
              <MenuItem
                onClick={(event) => {
                  event.stopPropagation();
                  setRequestDialog({
                    requestMethod: 'POST',
                    requestPath: '/gamification/event',
                    requestQuery: undefined,
                    requestBody: JSON.stringify({
                      eventId: 12345678,
                      source: 'SAVINGS_GOALS',
                      eventType: 'COMPLETE',
                      eventOn: '2021-04-06',
                    }),
                  });
                  setShowRequestDialog(true);
                }}
              >
                Savings Goal for Achievement
              </MenuItem>,
              <MenuItem
                onClick={(event) => {
                  event.stopPropagation();
                  setRequestDialog({
                    requestMethod: 'POST',
                    requestPath: '/gamification/event',
                    requestQuery: undefined,
                    requestBody: JSON.stringify({
                      eventId: 12345678,
                      source: 'UAA',
                      eventType: 'LOGIN',
                      eventOn: '2021-08-01',
                    }),
                  });
                  setShowRequestDialog(true);
                }}
              >
                Login for Achievement
              </MenuItem>,
              <MenuItem
                onClick={(event) => {
                  event.stopPropagation();
                  setRequestDialog({
                    requestMethod: 'POST',
                    requestPath: '/gamification/initialize',
                    requestQuery: undefined,
                    requestBody: undefined,
                  });
                  setShowRequestDialog(true);
                }}
              >
                Initialize Achievements
              </MenuItem>,
              <MenuItem
                onClick={(event) => {
                  event.stopPropagation();
                  setRequestDialog({
                    requestMethod: 'POST',
                    requestPath: '/gamification/internal/delete',
                    requestQuery: undefined,
                    requestBody: JSON.stringify({
                      ccpaRequestId: '111122',
                    }),
                  });
                  setShowRequestDialog(true);
                }}
              >
                CCPA delete Achievements
              </MenuItem>,
              <MenuItem
                onClick={(event) => {
                  event.stopPropagation();
                  setRequestDialog({
                    requestMethod: 'POST',
                    requestPath: '/gamification/initialize',
                    requestQuery: {
                      reinitialize: true,
                    },
                    requestBody: undefined,
                  });
                  setShowRequestDialog(true);
                }}
              >
                Reinitialize Achievements
              </MenuItem>,
              <MenuItem
                onClick={(event) => {
                  event.stopPropagation();
                  setRequestDialog({
                    requestMethod: 'POST',
                    requestPath: '/logos/tickers',
                    requestBody: JSON.stringify({ tickers: ['AAPL', 'QKN', '^BTC'] }, null, 2),
                  });
                  setShowRequestDialog(true);
                }}
              >
                Get Ticker Logos
              </MenuItem>,
              creditProfileData?.id && (
                <MenuItem
                  onClick={(event) => {
                    event.stopPropagation();
                    dispatch(deleteCreditProfile({ creditProfileRegistrationId: creditProfileData.id }));
                  }}
                >
                  Delete Credit Profile
                </MenuItem>
              ),
            ]}
          >
            Request
          </NestedMenuItem>

          <Divider />

          <NestedMenuItem
            dense
            id='dispatch-menu'
            // @ts-expect-error FIXME TS7006
            onClick={(event) => {
              if (event.target === event.currentTarget) setShowDispatchDialog(true);
              toggleMenu(false);
            }}
            menuItems={[
              <NestedMenuItem
                id='accounts'
                dense
                menuItems={[
                  <MenuItem
                    id='accounts-get'
                    dense
                    onClick={(event) => {
                      event.stopPropagation();
                      setDispatchDialog({
                        type: String(accountsActions.getAccounts),
                        payload: undefined,
                      });
                      setShowDispatchDialog(true);
                    }}
                  >
                    {String(accountsActions.getAccounts)}
                  </MenuItem>,
                  <MenuItem
                    id='accounts-create'
                    dense
                    onClick={(event) => {
                      event.stopPropagation();
                      setDispatchDialog({
                        type: String(accountsActions.createAccount),
                        payload: accountsTypes.mkAccount('BANK', {
                          clientId: uuidv4().toUpperCase(),
                        }),
                      });
                      setShowDispatchDialog(true);
                    }}
                  >
                    {String(accountsActions.createAccount)}
                  </MenuItem>,
                  <MenuItem
                    id='accounts-update'
                    dense
                    onClick={(event) => {
                      event.stopPropagation();
                      const accounts = accountsSelectors.getResourcesById(store.getState());
                      const account = accounts.first() || accountsTypes.mkAccount('BANK');
                      setDispatchDialog({
                        type: String(accountsActions.updateAccount),
                        payload: accountsTypes.mkAccount(account.type, account.toJS() as accountsTypes.AccountProps),
                      });
                      setShowDispatchDialog(true);
                    }}
                  >
                    {String(accountsActions.updateAccount)}
                  </MenuItem>,
                ]}
              >
                Accounts
              </NestedMenuItem>,
              <NestedMenuItem
                id='dispatch-transactions'
                dense
                menuItems={[
                  <MenuItem
                    id='transactions-get'
                    dense
                    onClick={(event) => {
                      event.stopPropagation();
                      setDispatchDialog({
                        type: String(transactionsActions.getTransactions),
                        payload: undefined,
                      });
                      setShowDispatchDialog(true);
                    }}
                  >
                    {String(transactionsActions.getTransactions)}
                  </MenuItem>,
                  <MenuItem
                    id='transactions-update'
                    dense
                    onClick={(event) => {
                      event.stopPropagation();
                      const transactions = transactionsSelectors.getResourcesById(store.getState());
                      const transaction = transactions.first() || transactionsTypes.mkCashFlowTransaction();
                      setDispatchDialog({
                        type: String(transactionsActions.updateTransaction),
                        payload: transactionsTypes.mkCashFlowTransaction(
                          transaction.toJS() as transactionsTypes.TransactionProps,
                        ),
                      });
                      setShowDispatchDialog(true);
                    }}
                  >
                    {String(transactionsActions.updateTransaction)}
                  </MenuItem>,
                ]}
              >
                Transactions
              </NestedMenuItem>,
              <NestedMenuItem
                id='dispatch-logos'
                dense
                menuItems={[
                  <MenuItem
                    id='logos-get-selected'
                    dense
                    onClick={(event) => {
                      event.stopPropagation();
                      setDispatchDialog({
                        type: String(tickerLogosActions.getTickerLogosSelected),
                        payload: Record<{ tickers?: string[] }>({ tickers: undefined })({
                          tickers: ['AAPL', 'GOOGL', 'MSFT'],
                        }),
                      });
                      setShowDispatchDialog(true);
                    }}
                  >
                    {String(tickerLogosActions.getTickerLogosSelected)}
                  </MenuItem>,
                  <MenuItem
                    id='logos-get-auto'
                    dense
                    onClick={(event) => {
                      event.stopPropagation();
                      setDispatchDialog({
                        type: String(tickerLogosActions.getTickerLogos),
                      });
                      setShowDispatchDialog(true);
                    }}
                  >
                    {String(tickerLogosActions.getTickerLogos)}
                  </MenuItem>,
                ]}
              >
                Logos
              </NestedMenuItem>,
            ]}
          >
            Dispatch
          </NestedMenuItem>

          <Divider />

          <MenuItem
            onClick={() => {
              const elements = document.querySelectorAll('[sharedcomponentid]');
              elements.forEach((node) => {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                const id = node.attributes.sharedcomponentid.value;
                if (window.getComputedStyle(node)?.position === 'static') {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  node.style.position = 'relative'; // eslint-disable-line
                }
                const anchorElement = document.createElement('div');
                node.appendChild(anchorElement);
                // eslint-disable-next-line react/no-deprecated
                ReactDOM.render(React.createElement(ReusableComponentTag, { id }), anchorElement);
              });
            }}
            dense
          >
            Find Shared Components
          </MenuItem>
          <Divider />
          <NestedMenuItem
            key='Crash-Menu'
            style={{ opacity: 0.85 }}
            menuItems={[
              <MenuItem
                dense
                key='simulate-JS'
                onClick={() => {
                  throw new Error('Simulate JS unhandled exception!');
                }}
              >
                Simulate JS unhandled exception
              </MenuItem>,
              <MenuItem dense key='simulate-REACT' onClick={() => window.crashUI()}>
                Simulate REACT unhandled exception
              </MenuItem>,
              <MenuItem
                dense
                key='simulate-JS-handled'
                onClick={() => {
                  try {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    null.simulateHandledException();
                  } catch (e) {
                    crashReporterInterface.reportError(e, { custom: { some: 'data' } });
                  }
                }}
              >
                Simulate JS handled exception
              </MenuItem>,
              <MenuItem
                dense
                key='simulate-unhandled-rejection'
                onClick={() => {
                  const promise = new Promise((_resolve, reject) => {
                    // throw new Error('simulate unhandled rejection');
                    // reject(Error('simulate promise reject'));
                    const invalidError = Error('simulate unhandled rejection');
                    invalidError.name = 'InvalidError';
                    reject(invalidError);
                  });
                  // eslint-disable-next-line promise/catch-or-return
                  promise.then(() => logger.debug('promiss fulfilled'));
                  // .catch((error) => assert(false, error))
                  // .finally(() => logger.debug('promiss finally handler'))
                }}
              >
                Simulate unhandled rejection (ignored)
              </MenuItem>,
              <MenuItem
                dense
                key='simulate-user-agent-error'
                onClick={() => {
                  Object.defineProperty(navigator, 'userAgent', {
                    value: 'hello',
                    configurable: false,
                  });
                  // TypeError: Cannot redefine property: userAgent
                  Object.defineProperty(navigator, 'userAgent', {
                    value: 'world',
                  });
                }}
              >
                Simulate userAgent exception
              </MenuItem>,
              <MenuItem
                dense
                key='simulate-assert1'
                onClick={() => {
                  assert(false, 'test assert1');
                }}
              >
                Simulate assert1
              </MenuItem>,
              <MenuItem
                dense
                key='simulate-assert2'
                onClick={() => {
                  assert(false, 'test assert2');
                }}
              >
                Simulate assert2
              </MenuItem>,
              <MenuItem
                dense
                key='simulate-verify'
                onClick={() => {
                  verify(false, 'test verify');
                }}
              >
                Simulate verify
              </MenuItem>,
              <MenuItem
                dense
                key='simulate-saga-crash'
                onClick={() => {
                  dispatch(appRefreshAll(undefined, { props: { crashSaga: true } }));
                }}
              >
                Simulate saga crash
              </MenuItem>,
              <MenuItem
                dense
                key='simulate-reducer-crash'
                onClick={() => {
                  dispatch(appRefreshAll(undefined, { props: { crashReducer: true } }));
                }}
              >
                Simulate reducer crash
              </MenuItem>,
              <MenuItem
                dense
                key='simulate-local-storage-full'
                onClick={() => {
                  const pattern =
                    'full of junk full of junk full of junk full of junk full of junk full of junk full of junk';
                  try {
                    // eslint-disable-next-line no-constant-condition
                    for (let index = 1; true; index++) {
                      // eslint-disable-line no-constant-condition
                      localStorage.setItem(String(index), pattern);
                    }
                  } catch (_e) {
                    try {
                      for (let length = 0; length < pattern.length; length++) {
                        // eslint-disable-line no-constant-condition
                        localStorage.setItem('0', pattern.slice(0, length));
                      }
                    } catch (e) {
                      assert(false, e);
                    }
                  }
                }}
              >
                Simulate localStorage full
              </MenuItem>,
              <MenuItem dense key='suppress-window'>
                <ListItemText primary='Suppress Error Alert (show Uh Oh page)' />
                <Switch
                  size='small'
                  color='primary'
                  onChange={() => {
                    localPreferences.setSuppressWindowError(!localPreferences.getSuppressWindowError());
                    update();
                  }}
                  checked={localPreferences.getSuppressWindowError()}
                />
              </MenuItem>,
              <MenuItem dense key='disable-bugsnag'>
                <ListItemText primary='Disable Bugsnag (restart required)' />
                <Switch
                  size='small'
                  color='primary'
                  onChange={() => {
                    localPreferences.setDisableBugsnag(!localPreferences.getDisableBugsnag());
                    update();
                  }}
                  checked={localPreferences.getDisableBugsnag()}
                />
              </MenuItem>,
              <MenuItem dense key='ignore-bugsnag'>
                <ListItemText primary='Ignore Bugsnag Reports' />
                <Switch
                  size='small'
                  color='primary'
                  onChange={() => {
                    localPreferences.setIgnoreBugsnagReport(!localPreferences.getIgnoreBugsnagReport());
                    update();
                  }}
                  checked={localPreferences.getIgnoreBugsnagReport()}
                />
              </MenuItem>,
              <MenuItem dense key='suppress-assert'>
                <ListItemText primary='Suppress Asserts' />
                <Switch
                  size='small'
                  color='primary'
                  onChange={() => {
                    localPreferences.setSuppressAssertPopups(!localPreferences.getSuppressAssertPopups());
                    update();
                  }}
                  checked={localPreferences.getSuppressAssertPopups()}
                />
              </MenuItem>,
            ]}
          >
            Crash
            <br />({getBuildConfig().bugsnag_api_key})
          </NestedMenuItem>
          <Divider />
          <NestedMenuItem
            key='auth-menu'
            id='auth-menu'
            arrowid='auth-menu-arrow'
            menuItems={[
              <MenuItem
                dense
                key='clear-access-token-key'
                id='clear-access-token'
                onClick={() =>
                  dispatch(
                    authActions.updateAuth({
                      accessToken: null,
                      accessTokenExpired: null,
                    }),
                  )
                }
              >
                Clear access token -&nbsp;
                <span
                  style={{
                    display: 'inline-block',
                    width: 300,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {authSession.accessToken}
                </span>
              </MenuItem>,
              <MenuItem
                dense
                key='clear-refresh-token-key'
                id='clear-refresh-token'
                onClick={() =>
                  dispatch(
                    authActions.updateAuth({
                      refreshToken: null,
                      refreshTokenExpired: null,
                    }),
                  )
                }
              >
                Clear refresh token -&nbsp;
                <span
                  style={{
                    display: 'inline-block',
                    width: 300,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {authSession.refreshToken}
                </span>
              </MenuItem>,
              <MenuItem
                dense
                key='clear-dataset-key'
                id='clear-dataset'
                onClick={() =>
                  dispatch(
                    authActions.updateAuth({
                      datasetId: null,
                    }),
                  )
                }
              >
                Clear dataset -&nbsp;
                <span
                  style={{
                    display: 'inline-block',
                    width: 300,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {authSession.datasetId}
                </span>
              </MenuItem>,
              <Divider />,
              <MenuItem
                dense
                key='expire-access-token-key'
                id='expire-access-token'
                onClick={() => dispatch(authActions.invalidateAccessToken())}
              >
                Expire access token (
                {DateTime.fromISO(authSession.accessTokenExpired).toLocaleString(DateTime.DATETIME_MED)})
              </MenuItem>,
              <MenuItem
                dense
                key='expire-refresh-token-key'
                id='expire-refresh-token'
                onClick={() => dispatch(authActions.invalidateRefreshToken())}
              >
                Expire refresh token (
                {DateTime.fromISO(authSession.refreshTokenExpired).toLocaleString(DateTime.DATETIME_MED)})
              </MenuItem>,
              <Divider />,
              <MenuItem
                dense
                key='corrupt-access-token-key'
                id='corrupt-access-token'
                onClick={() =>
                  dispatch(
                    authActions.updateAuth({
                      accessToken: 'corrupted!!!',
                    }),
                  )
                }
              >
                Corrupt access token -&nbsp;
                <span
                  style={{
                    display: 'inline-block',
                    width: 300,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {authSession.accessToken}
                </span>
              </MenuItem>,
              <MenuItem
                dense
                key='expired-access-token-key'
                id='expired-access-token'
                onClick={() =>
                  dispatch(
                    authActions.updateAuth({
                      accessToken:
                        'eyJhbGciOiJSUzI1NiIsImprdSI6Imh0dHBzOi8vbG9jYWxob3N0OjgwODAvdWFhL3Rva2VuX2tleXMiLCJraWQiOiJrZXktaWQtMSIsInR5cCI6IkpXVCJ9.eyJqdGkiOiI1NmVkOWU0ODUyOGI0ZDU4YTYxM2U4N2JlOTAyZDk2NCIsInN1YiI6ImU5NDllNzRjLTA4NDctNDM0Ny05ZTFlLTk2Y2ExOTFmN2Y2MSIsInNjb3BlIjpbIm9wZW5pZCIsInFjcy5tZSIsInVhYS51c2VyIl0sImNsaWVudF9pZCI6ImFjbWVfd2ViIiwiY2lkIjoiYWNtZV93ZWIiLCJhenAiOiJhY21lX3dlYiIsImdyYW50X3R5cGUiOiJhdXRob3JpemF0aW9uX2NvZGUiLCJ1c2VyX2lkIjoiZTk0OWU3NGMtMDg0Ny00MzQ3LTllMWUtOTZjYTE5MWY3ZjYxIiwib3JpZ2luIjoidWFhIiwidXNlcl9uYW1lIjoiYWI2MzdmMjEtZGI1OC00MzJiLWE1NGItNDNhOTU5N2JmNTljIiwiZW1haWwiOiJhYjYzN2YyMS1kYjU4LTQzMmItYTU0Yi00M2E5NTk3YmY1OWNAdWFhLnF1aWNrZW4uY29tIiwiYXV0aF90aW1lIjoxNjQ2MjYzNDIxLCJyZXZfc2lnIjoiYTg2YjM4NjAiLCJpYXQiOjE2NDYzNTQxMzIsImV4cCI6MTY0NjM1NzczMiwiaXNzIjoiaHR0cDovL2xvY2FsaG9zdDo4MDgwL3VhYS9vYXV0aC90b2tlbiIsInppZCI6InVhYSIsImF1ZCI6WyJxY3MiLCJ1YWEiLCJvcGVuaWQiLCJhY21lX3dlYiJdfQ.LB77tgzLAIeOHY7yv1h6iE1X8G1iHaj7b-WWWy9TMftx3AOOuHcBfa8RTHA0w1IASHSqJ9ldMygp1fE0Vj77Zck3OtqoqtkMhlZCWwQV5X7tpwgNdQULBxm1zR2kXAFoGwu3lYB2y-B21hCbOJL3M6bJGp52jKQGrEou5UbeGY7auGYe8jWDyGd_NimSz-ZvIqeODwwmj9La3g3X152QuLuPl-5Jw1VUJyLPH2WMug-iZktvt9i3csV36N0nOMOVa0mecra0H4dNvrV3X4OWvslCIbdrRxeRfaSZBs3NifTFhJ6ulhPQb64Mhsyfnb8cvaaWcpr_xsRzdAx31YsXfQ',
                    }),
                  )
                }
              >
                Expired access token -&nbsp;
                <span
                  style={{
                    display: 'inline-block',
                    width: 300,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {authSession.accessToken}
                </span>
              </MenuItem>,
              <MenuItem
                dense
                key='corrupt-refresh-token-key'
                id='corrupt-refresh-token'
                onClick={() =>
                  dispatch(
                    authActions.updateAuth({
                      refreshToken: 'corrupted!!!',
                    }),
                  )
                }
              >
                Corrupt refresh token -&nbsp;
                <span
                  style={{
                    display: 'inline-block',
                    width: 300,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {authSession.refreshToken}
                </span>
              </MenuItem>,
              <MenuItem
                dense
                key='expired-refresh-token-key'
                id='expired-refresh-token'
                onClick={() =>
                  dispatch(
                    authActions.updateAuth({
                      accessToken:
                        'eyJhbGciOiJSUzI1NiIsImprdSI6Imh0dHBzOi8vbG9jYWxob3N0OjgwODAvdWFhL3Rva2VuX2tleXMiLCJraWQiOiJrZXktaWQtMSIsInR5cCI6IkpXVCJ9.eyJqdGkiOiJiOGEyMzIxZDRlZWU0YmFmOWZiNzAzNjI3YTVlZWM0MS1yIiwic3ViIjoiODM0YzgxNjUtYjQzNi00MzY4LTg2ZjctMmRkZTUxOWMwMTJkIiwiaWF0IjoxNjQ1NzI3OTU4LCJleHAiOjE3NDAzMzU5NTgsImNpZCI6ImFjbWVfd2ViIiwiY2xpZW50X2lkIjoiYWNtZV93ZWIiLCJpc3MiOiJodHRwOi8vbG9jYWxob3N0OjgwODAvdWFhL29hdXRoL3Rva2VuIiwiemlkIjoidWFhIiwiYXVkIjpbInFjcyIsInVhYSIsIm9wZW5pZCIsImFjbWVfd2ViIl0sImdyYW50ZWRfc2NvcGVzIjpbIm9wZW5pZCIsInFjcy5tZSIsInVhYS51c2VyIl0sImF1dGhfdGltZSI6MTY0NTcyNzk1OCwiZ3JhbnRfdHlwZSI6ImF1dGhvcml6YXRpb25fY29kZSIsInVzZXJfbmFtZSI6IjNiYmRmZTYyLWMwZmItNGI5Mi05ODU4LTRmMWUwMWQ5Mzc3YSIsIm9yaWdpbiI6InVhYSIsInVzZXJfaWQiOiI4MzRjODE2NS1iNDM2LTQzNjgtODZmNy0yZGRlNTE5YzAxMmQiLCJyZXZfc2lnIjoiYWI3MzFiNmEifQ.ItkoYfKH4eoDslY5WoF8flRnxZz8277R2N70KdCDqPz8xqrpej4J21BaYbeyF8qwtloYQhYm_iCowMuf1tH4f952fthFY0MCLqeZjGYZec96OEhxZSEkuytj2SPeNV-3zwzbTc23RrUcWqYMa2S9h6bc5hY6gWKI5DtqGPdCc_wgp2tigRam-Ehrr-dU3RU0WSpcwvHp1-NE0UA_dCYX72FUm5YYEGXdTto_E6HRX3_VoAGPJqhrALrxG069vryIXcaN1binCQrCMGLFLKkEg9c6eoZfS8X7ei7H7SkOLhB8SIT3RUurCc4H3D_qEc1lursEmwkOIRQLQSCqT9JB9g',
                    }),
                  )
                }
              >
                Expired refresh token -&nbsp;
                <span
                  style={{
                    display: 'inline-block',
                    width: 300,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {authSession.refreshToken}
                </span>
              </MenuItem>,
              <MenuItem
                dense
                key='corrupt-dataset-key'
                id='corrupt-dataset'
                onClick={() => dispatch(authActions.updateAuth({ datasetId: 'corrupted!!!' }))}
              >
                Corrupt data set ({authSession.datasetId})
              </MenuItem>,
              <MenuItem
                dense
                key='not-existing-dataset-key'
                id='no-existing-dataset'
                onClick={() => dispatch(authActions.updateAuth({ datasetId: '12345' }))}
              >
                Set not existing data set ({authSession.datasetId})
              </MenuItem>,
              <MenuItem
                dense
                key='cross-talk-dataset-key'
                id='cross-talk-dataset'
                onClick={() => dispatch(authActions.updateAuth({ datasetId: '304249255459973889' }))}
              >
                Set another user data set ({authSession.datasetId})
              </MenuItem>,
              <Divider />,
              <MenuItem
                dense
                key='use-refresh-token'
                id='use-refresh-token'
                onClick={() => dispatch(authActions.exchangeRefreshToken())}
              >
                Use Refresh Token&nbsp;
                <span
                  style={{
                    display: 'inline-block',
                    width: 300,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  ({authSession.refreshToken})
                </span>
              </MenuItem>,
            ]}
          >
            auth
          </NestedMenuItem>
          <Divider />
          <NestedMenuItem
            key='NPS-menu'
            style={{ opacity: 0.85 }}
            menuItems={[
              <MenuItem dense key='survey-request'>
                <ListItemText primary='Survey Requested' />
                <Switch
                  size='small'
                  color='primary'
                  disabled={!authSession}
                  onChange={(event, value) => {
                    dispatch(authActions.updateAuth({ surveyRequested: value }));
                  }}
                  checked={Boolean(authSession?.surveyRequested)}
                />
              </MenuItem>,
              <MenuItem
                dense
                key='check-for-survey'
                onClick={() => dispatch(npsActions.checkIfNPSSurveyRequestedAction())}
              >
                Check for Survey Now
              </MenuItem>,
            ]}
          >
            NPS
          </NestedMenuItem>
          <Divider />
          <NestedMenuItem
            key='Log-Level-Menu'
            menuItems={[
              <MenuItem dense key='local-override'>
                <ListItemText primary='Local Override' />
                <Switch
                  size='small'
                  color='primary'
                  onChange={(event, value) => {
                    if (!value) {
                      localPreferences.removeItem('loggingLevel');
                      update();
                    }
                  }}
                  checked={Boolean(localPreferences.getItem('loggingLevel'))}
                />
              </MenuItem>,
              <Divider />,
              ...Object.entries(loggerConfigurator.LogLevelsEnum).map(([key, value]) => (
                <MenuItem
                  dense
                  key={key}
                  onClick={() => {
                    loggerConfigurator.storeLoggingLevel(loggerConfigurator.LogLevelsEnum[key]);
                    update();
                  }}
                >
                  {`${value}`}
                  <Radio
                    checked={loggerConfigurator.retrieveLoggingLevel() === loggerConfigurator.LogLevelsEnum[key]}
                  />
                  {key}+{key === 'INFO' ? ' (default in prod)' : ''}
                </MenuItem>
              )),
              <Divider />,
              <MenuItem dense key='debug-test' onClick={() => logger.debug('test loging: %s', 'debug')}>
                trigger debug logging
              </MenuItem>,
              <MenuItem dense key='log-test' onClick={() => logger.log('test logging: %s', 'log')}>
                trigger log logging
              </MenuItem>,
              <MenuItem dense key='info-test' onClick={() => logger.info('test logging: %s', 'info')}>
                trigger info log
              </MenuItem>,
              <MenuItem dense key='warn-test' onClick={() => logger.warn('test logging: %s', 'warn')}>
                trigger warn log
              </MenuItem>,
              <MenuItem dense key='error-test' onClick={() => logger.error('test logging: %s', 'error')}>
                trigger error log
              </MenuItem>,
              <MenuItem dense key='fatal-test' onClick={() => logger.fatal('test logging: %s', 'fatal')}>
                trigger fatal log
              </MenuItem>,
            ]}
          >
            Log level ({loggerConfigurator.retrieveLoggingLevel()} -&nbsp;
            {
              Object.entries(loggerConfigurator.LogLevelsEnum).find(
                ([_key, value]) => value === loggerConfigurator.retrieveLoggingLevel(),
              )?.[0]
            }
            )
          </NestedMenuItem>
          <Divider />
          <NestedMenuItem
            key='subscription-messaging'
            menuItems={[
              ...Object.values(subscriptionsSelectors.subscriptionMessages).map((subscriptionMessage: string) => (
                <MenuItem
                  key={String(subscriptionMessage)}
                  id={String(subscriptionMessage)}
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore TS2345
                  onClick={() => setSubscriptionMessageMock(subscriptionMessage)}
                >
                  {String(subscriptionMessage)}
                </MenuItem>
              )),
              <Divider />,
              <MenuItem
                key='reset-popup'
                id='reset-popup'
                onClick={() =>
                  dispatch(
                    preferencesActions.setPreference({
                      section: 'shared',
                      group: 'user',
                      preference: {
                        subscriptionMessageDismissedOn: DateTime.utc().startOf('day').minus({ days: 31 }).toISODate(),
                      },
                    }),
                  )
                }
              >
                Reset Popup ({popupMessageDismissedOnISO})
              </MenuItem>,
            ]}
          >
            Subscription Messaging ({subscriptionMessageMock})
          </NestedMenuItem>
          <Divider />
          <ListItem key='offline-toggle'>
            <ListItemText primary='Offline Support' />
            <ListItemSecondaryAction>
              <Switch
                size='small'
                color='primary'
                onChange={(_event, value) => {
                  localPreferences.setOfflineSupport(value);
                  update();
                }}
                checked={localPreferences.getOfflineSupport()}
              />
            </ListItemSecondaryAction>
          </ListItem>
          <Divider />
          <ListItem key='sandbox-toggle'>
            <ListItemText primary='Sandbox' />
            <ListItemSecondaryAction>
              <Switch
                size='small'
                color='primary'
                onChange={(_event, value) => {
                  sessionStorageEx.setSandbox(value);
                  update();
                }}
                checked={sessionStorageEx.getSandbox()}
              />
            </ListItemSecondaryAction>
          </ListItem>
          <Divider />
          <ListItem key='render-count'>
            <ListItemText primary='Render Count' />
            <ListItemSecondaryAction>
              <Switch
                size='small'
                color='primary'
                onChange={(_event, value) => {
                  localPreferences.setItem('render-count', value);
                  update();
                }}
                checked={localPreferences.getItem('render-count') ?? true}
              />
            </ListItemSecondaryAction>
          </ListItem>
          <Divider />
          <ListItem key='react-scan'>
            <ListItemText primary='React Scan' />
            <ListItemSecondaryAction>
              <Switch
                size='small'
                color='primary'
                onChange={(_event, value) => {
                  update();
                  localPreferences.setItem('react-scan-options', {
                    ...localPreferences.getItem('react-scan-options'),
                    showToolbar: value,
                    enabled: value,
                  });
                  window.location.reload();
                }}
                checked={localPreferences.getItem('react-scan-options')?.showToolbar ?? true}
              />
            </ListItemSecondaryAction>
          </ListItem>
          <Divider />
          <MenuItem dense onClick={() => logger.debug('current DB state:\n', store.getState())}>
            Dump DB to console
          </MenuItem>
          <Divider />
          <MenuItem
            dense
            key='hide-dev-menu'
            onClick={() => {
              toggleMenu(false);
              setShowDevMenuButton(false);
            }}
          >
            Hide Dev Menu
          </MenuItem>
          <Divider />
          <MenuItem
            key='test-menu'
            onClick={() => {
              toggleMenu(false);
              onTest();
            }}
          >
            Test (Cmd + B)
          </MenuItem>
        </Menu>
      )}
    </>
  );
});

DevMenu.displayName = 'DevMenu';
export default DevMenu;
